import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEO from '@/components/feature/SEO';
import { blogArticles } from '@/mocks/blog';

const siteUrl =
  typeof __VITE_SITE_URL__ !== 'undefined' && __VITE_SITE_URL__
    ? __VITE_SITE_URL__.replace(/\/+$/, '')
    : 'https://mulebuyspreadsheets.motorcycles';

export default function BlogPostPage() {
  const { slug } = useParams<{ slug: string }>();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  const article = blogArticles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <SEO
          title="Article Not Found – Mulebuy Spreadsheet Database"
          description="The requested mulebuy spreadsheet article could not be found. Browse our complete collection of guides, comparisons, and tutorials."
          canonicalPath={`/blog/${slug || ''}`}
          noindex={true}
        />
        <Navbar />
        <main className="pt-32 pb-16">
          <div className="max-w-3xl mx-auto px-6 text-center">
            <h1 className="font-heading text-3xl font-bold text-foreground-950 mb-4">Article Not Found</h1>
            <Link to="/" className="text-primary-600 hover:text-primary-700 font-medium">
              Back to Mulebuy Spreadsheet
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const articleUrl = `${siteUrl}/blog/${article.slug}`;

  return (
    <div className="min-h-screen bg-background-50">
      <SEO
        title={`${article.title} – Mulebuy Spreadsheet Database`}
        description={article.excerpt}
        keywords={`mulebuy spreadsheet, ${article.category.toLowerCase()}, ${article.title.toLowerCase()}, mulebuy spreadsheet guide`}
        canonicalPath={`/blog/${article.slug}`}
        ogType="article"
        ogImage={article.imageUrl}
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'Article',
          headline: article.title,
          description: article.excerpt,
          image: article.imageUrl,
          author: {
            '@type': 'Organization',
            name: 'Mulebuy Spreadsheet Database',
          },
          publisher: {
            '@type': 'Organization',
            name: 'Mulebuy Spreadsheet Database',
            url: siteUrl,
          },
          datePublished: '2026-06-01',
          dateModified: '2026-06-22',
          mainEntityOfPage: {
            '@type': 'WebPage',
            '@id': articleUrl,
          },
        }}
      />
      <Navbar />
      <main>
        <article className="w-full pt-28 md:pt-32 pb-16 md:pb-20">
          <div className="max-w-3xl mx-auto px-6 md:px-10">
            <div className="mb-8">
              <Link
                to="/"
                className="inline-flex items-center gap-2 text-sm text-foreground-500 hover:text-foreground-950 transition-colors whitespace-nowrap"
              >
                <i className="ri-arrow-left-line"></i>
                Back to Mulebuy Spreadsheet
              </Link>
            </div>

            <div className="flex items-center gap-3 mb-4">
              <span className="px-3 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-label tracking-wider">
                {article.category}
              </span>
              <span className="text-xs text-foreground-400">{article.readTime}</span>
            </div>

            <h1 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950 leading-tight mb-4">
              {article.title}
            </h1>

            <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500 mb-8 pb-8 border-b border-background-200">
              <span className="flex items-center gap-1.5">
                <i className="ri-calendar-line text-sm w-4 h-4 flex items-center justify-center"></i>
                {article.date}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-time-line text-sm w-4 h-4 flex items-center justify-center"></i>
                {article.readTime}
              </span>
            </div>

            <div className="w-full aspect-[16/9] rounded-2xl overflow-hidden mb-10">
              <img
                src={article.imageUrl}
                alt={article.title}
                className="w-full h-full object-cover"
              />
            </div>

            <div>
              <p className="text-foreground-700 leading-relaxed mb-4">
                The <strong>mulebuy spreadsheet</strong> ecosystem continues to evolve rapidly in 2026, offering sourcing professionals unprecedented access to organized product data and supplier information. This article explores {article.title.toLowerCase()} in comprehensive detail, providing actionable insights for both beginners and experienced users of the mulebuy spreadsheet platform.
              </p>
              <p className="text-foreground-700 leading-relaxed mb-6">
                Whether you are sourcing fashion items, electronics, or lifestyle products, having a well-structured <Link to="/" className="text-primary-600 hover:text-primary-700 font-medium">mulebuy spreadsheet</Link> is essential for staying competitive in the fast-paced world of cross-border e-commerce. The right spreadsheet organization can mean the difference between profitable sourcing decisions and costly mistakes.
              </p>

              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mt-10 mb-4">
                Key Insights on {article.title}
              </h2>
              <p className="text-foreground-700 leading-relaxed mb-4">
                Understanding the nuances of your mulebuy spreadsheet setup is crucial for maximizing its potential. Every sourcing professional should focus on three core areas: data accuracy, update frequency, and category organization. When these elements align within your spreadsheet, you create a powerful sourcing engine that drives better business outcomes.
              </p>
              <p className="text-foreground-700 leading-relaxed mb-4">
                The community-driven nature of the mulebuy spreadsheet ecosystem means that best practices are constantly being refined. By staying engaged with the latest guides and resources available through the <Link to="/" className="text-primary-600 hover:text-primary-700 font-medium">mulebuy spreadsheet</Link> hub, you ensure your sourcing workflow remains at the cutting edge.
              </p>

              <blockquote className="border-l-4 border-primary-400 pl-6 py-4 my-8 bg-primary-50/50 rounded-r-xl">
                <p className="text-foreground-700 italic leading-relaxed">
                  "A well-maintained mulebuy spreadsheet is not just a data repository &mdash; it is a strategic asset that compounds in value as your product catalog grows and your supplier relationships deepen."
                </p>
              </blockquote>

              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mt-10 mb-4">
                Practical Applications
              </h2>
              <p className="text-foreground-700 leading-relaxed mb-4">
                Implementing the strategies covered in this guide requires a systematic approach. Start by auditing your current mulebuy spreadsheet structure, identifying gaps in your data collection, and establishing a regular maintenance schedule. Small improvements applied consistently yield compounding results over time.
              </p>

              <div className="my-8 overflow-x-auto">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="bg-primary-100">
                      <th className="py-3 px-4 text-sm font-semibold text-primary-900 rounded-l-lg">Factor</th>
                      <th className="py-3 px-4 text-sm font-semibold text-primary-900">Impact</th>
                      <th className="py-3 px-4 text-sm font-semibold text-primary-900 rounded-r-lg">Priority</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b border-background-200">
                      <td className="py-3 px-4 text-sm text-foreground-700 font-medium">Data Accuracy</td>
                      <td className="py-3 px-4 text-sm text-foreground-600">Directly affects sourcing decisions</td>
                      <td className="py-3 px-4 text-sm text-accent-700 font-semibold">High</td>
                    </tr>
                    <tr className="border-b border-background-200">
                      <td className="py-3 px-4 text-sm text-foreground-700 font-medium">Update Frequency</td>
                      <td className="py-3 px-4 text-sm text-foreground-600">Keeps information current and relevant</td>
                      <td className="py-3 px-4 text-sm text-accent-700 font-semibold">High</td>
                    </tr>
                    <tr>
                      <td className="py-3 px-4 text-sm text-foreground-700 font-medium">Category Organization</td>
                      <td className="py-3 px-4 text-sm text-foreground-600">Improves navigation and searchability</td>
                      <td className="py-3 px-4 text-sm text-foreground-600 font-medium">Medium</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mt-10 mb-4">
                Best Practices for Your Workflow
              </h2>
              <ul className="space-y-3 mb-6 list-disc pl-6">
                <li className="text-foreground-700 leading-relaxed">
                  <strong className="text-foreground-950">Maintain Consistent Formatting:</strong> Standardize your data entry conventions across all categories in your mulebuy spreadsheet to ensure seamless filtering and sorting.
                </li>
                <li className="text-foreground-700 leading-relaxed">
                  <strong className="text-foreground-950">Regular Backup Schedule:</strong> Set automated backups of your mulebuy spreadsheet to prevent data loss and maintain version history.
                </li>
                <li className="text-foreground-700 leading-relaxed">
                  <strong className="text-foreground-950">Cross-Reference Suppliers:</strong> Use your spreadsheet to track supplier performance metrics and make data-driven partnership decisions.
                </li>
                <li className="text-foreground-700 leading-relaxed">
                  <strong className="text-foreground-950">Community Engagement:</strong> Stay connected with the mulebuy spreadsheet community to learn about new features and optimization techniques.
                </li>
              </ul>

              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mt-10 mb-4">
                Frequently Asked Questions
              </h2>
              <div className="space-y-4 mb-10">
                <div className="bg-background-100 rounded-xl p-6">
                  <h3 className="font-heading text-lg font-bold text-foreground-950 mb-2">
                    How do I get started with this approach?
                  </h3>
                  <p className="text-foreground-600 text-sm leading-relaxed">
                    Begin by visiting the <Link to="/" className="text-primary-600 hover:text-primary-700 font-medium">mulebuy spreadsheet</Link> homepage and exploring the available categories. From there, you can navigate to specific product categories and start building your personalized sourcing database.
                  </p>
                </div>
                <div className="bg-background-100 rounded-xl p-6">
                  <h3 className="font-heading text-lg font-bold text-foreground-950 mb-2">
                    Can I customize my mulebuy spreadsheet layout?
                  </h3>
                  <p className="text-foreground-600 text-sm leading-relaxed">
                    Absolutely. The mulebuy spreadsheet framework is designed to be flexible. You can add custom columns, create category-specific views, and tailor the organization to match your unique sourcing workflow requirements.
                  </p>
                </div>
                <div className="bg-background-100 rounded-xl p-6">
                  <h3 className="font-heading text-lg font-bold text-foreground-950 mb-2">
                    Where can I find more mulebuy spreadsheet resources?
                  </h3>
                  <p className="text-foreground-600 text-sm leading-relaxed">
                    Check out the complete <Link to="/what-is-mulebuy-spreadsheet" className="text-primary-600 hover:text-primary-700 font-medium">mulebuy spreadsheet guide</Link>, browse our blog articles, or visit the shopping guide section for curated recommendations and tips.
                  </p>
                </div>
              </div>

              <div className="bg-primary-50 rounded-2xl p-8 mb-10">
                <h2 className="font-heading text-xl md:text-2xl font-bold text-primary-900 mb-3">
                  Ready to Level Up Your Sourcing?
                </h2>
                <p className="text-primary-700 text-sm leading-relaxed mb-6">
                  Explore the complete mulebuy spreadsheet database and discover how organized data can transform your product sourcing workflow.
                </p>
                <div className="flex flex-col sm:flex-row gap-3">
                  <Link
                    to="/"
                    className="inline-flex items-center gap-2 px-7 py-3 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    Back to Mulebuy Spreadsheet
                    <i className="ri-arrow-right-line"></i>
                  </Link>
                  <a
                    href="https://kaspreadsheet.com/"
                    target="_blank"
                    rel="noopener nofollow"
                    className="inline-flex items-center gap-2 px-7 py-3 rounded-full border border-primary-300 text-primary-700 text-sm font-semibold hover:bg-primary-100 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    Visit kaspreadsheet.com
                    <i className="ri-external-link-line"></i>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </article>
      </main>
      <Footer />
    </div>
  );
}